import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
} from "@tanstack/react-table";
import { useState } from "react";

import { Button } from "@/components/ui/button";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}

const DataTableComercialInvoicing = <TData, TValue>({
  columns,
  data,
}: DataTableProps<TData, TValue>) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const tableComercialInvoicing = useReactTable({
    data: Array.isArray(data) ? data : [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),

    state: {
      sorting,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 2,
      },
    },
  });

  const pageIndex = tableComercialInvoicing.getState().pagination.pageIndex;
  const pageCount = tableComercialInvoicing.getPageCount();

  return (
    <div className="flex flex-col max-h-[550px] min-w-full">
      <div className="h-fit overflow-y-auto shadow-md">
        <Table className="min-w-full">
          <TableHeader>
            {tableComercialInvoicing.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className={
                        header.id === "actions"
                          ? "w-[50px] max-w-[50px] truncate"
                          : undefined
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {tableComercialInvoicing.getRowModel().rows?.length ? (
              tableComercialInvoicing.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="truncate">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No hay resultados.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between py-4">
        <div>
          <span className="text-sm font-[Inter]">
            Página {pageIndex + 1} de {pageCount}
          </span>
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            className="ml-auto border-gray-200 hover:bg-gray-100 text-black shadow-sm"
            size="sm"
            onClick={() => tableComercialInvoicing.previousPage()}
            disabled={!tableComercialInvoicing.getCanPreviousPage()}
          >
            Anterior
          </Button>
          <Button
            variant="outline"
            className="ml-auto border-gray-200 hover:bg-gray-100 text-black shadow-sm"
            size="sm"
            onClick={() => tableComercialInvoicing.nextPage()}
            disabled={!tableComercialInvoicing.getCanNextPage()}
          >
            Siguiente
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DataTableComercialInvoicing;
